<template>
  <page-container :title="title">
    <v-row class="ma-0 mb-2 pa-2 tab-header" align-content="center">
      <v-tabs v-model="tab" height="36">
        <v-tab>Contractor details</v-tab>
        <v-spacer></v-spacer>
        <feature name="people" variant="write" data="contractors">
          <v-btn
            tile
            v-show="contractorInfoChanged"
            class="button background-color--inactive color--foreground"
            @click="cancelChanges"
            :disabled="saving"
            >Cancel</v-btn
          >
          <v-btn
            tile
            color="primary"
            class="mx-2 button"
            v-show="contractorInfoChanged"
            :disabled="!contractorFormValid || saving"
            @click="saveEditedCompanyInfo"
            :loading="saving"
            >Save</v-btn
          >
          <v-btn icon @click="deleteContractor" :disabled="saving"
            ><v-icon>mdi-delete</v-icon></v-btn
          >
        </feature>
      </v-tabs>
    </v-row>
    <v-row>
      <v-col cols="8" class="pa-0 pl-3 pb-2">
        <v-sheet style="height: 100%">
          <v-container>
            <v-form v-model="contractorFormValid" ref="contractorForm">
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Contractor name </v-col>
                <v-col cols="8" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.companyName"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="
                      rules.required(editedContractorInfo.companyName) === true
                    "
                    :rules="[rules.required]"
                    required
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Email </v-col>
                <v-col cols="8" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.email"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="
                      rules.email(editedContractorInfo.email) === true
                    "
                    :rules="[rules.email]"
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Phone number </v-col>
                <v-col cols="8" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.phoneNumber"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="
                      rules.phoneNumber(editedContractorInfo.phoneNumber) ===
                      true
                    "
                    :rules="[rules.phoneNumber]"
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header">Address line 1</v-col>
                <v-col cols="8" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.address.line1"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="true"
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Address line 2 </v-col>
                <v-col cols="8" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.address.line2"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="true"
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Town / City </v-col>
                <v-col cols="4" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.address.city"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="true"
                  />
                </v-col>
              </v-row>
              <v-row align="center" class="ma-0">
                <v-col cols="4" class="details-header"> Postcode </v-col>
                <v-col cols="2" class="py-2">
                  <v-text-field
                    solo
                    flat
                    dense
                    v-model="editedContractorInfo.address.postcode"
                    class="details-field px-2"
                    background-color="transparent"
                    :hide-details="true"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-sheet>
      </v-col>
      <v-col cols="4" class="pa-0 pr-3 pl-2 pb-2">
        <v-sheet style="height: 100%">
          <v-row class="ma-0 py-4 px-6"
            ><span class="logo-header">Contractor logo</span></v-row
          >
          <image-upload
            @removeImage="imageToRemove"
            :imageURL.sync="editedContractorInfo.logoURL"
            :imageStyle="{ borderRadius: '50%' }"
            :imagePreview.sync="imagePreview"
          ></image-upload>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row class="ma-0">
      <v-card elevation="0" min-width="100%" class="my-4">
        <common-data-table
          ref="contactDataTable"
          title="Contacts"
          newActionTitle="Add contact"
          :headers="headers"
          :dataBasePath="dataBasePath"
          extraSortBy="primary"
          extraSortByDirection="desc"
          collectionName="contacts"
        >
          <template v-slot:newItemPopup="{ close, item, totalItems, formKey }">
            <common-editing-popup
              ref="addContactPopup"
              title="New contact"
              @close="close()"
              :canSubmit="formValid"
              :dataBasePath="dataBasePath"
              :showDefaultSuccessMsg="false"
              @submit="addContact"
              collectionName="contacts"
              :loading="addingContact"
              :originalItem="item ? item : { primary: totalItems === 0 }"
              :key="'contact-edit-' + (item ? item.id : formKey)"
            >
              <template v-slot:default="{ editedItem }">
                <v-form v-model="formValid" ref="form">
                  <v-container class="pa-0">
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          label="First name"
                          v-model="editedItem.firstName"
                          :rules="[rules.required]"
                        ></v-text-field>
                        <v-text-field
                          label="Last name"
                          v-model="editedItem.lastName"
                          :rules="[rules.required]"
                        ></v-text-field>
                        <v-text-field
                          label="Email"
                          type="email"
                          v-model="editedItem.email"
                          :rules="[rules.email]"
                        ></v-text-field>
                        <v-text-field
                          label="Contact number"
                          type="tel"
                          v-model="editedItem.phoneNumber"
                          :rules="[rules.phoneNumber]"
                        ></v-text-field>
                        <v-row class="ma-0" align="center">
                          <v-spacer></v-spacer>
                          <span class="primary-contact">Primary contact</span>
                          <v-checkbox
                            class="ml-2"
                            v-model="editedItem.primary"
                          ></v-checkbox>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </template>
            </common-editing-popup>
          </template>
        </common-data-table>
      </v-card>
    </v-row>
  </page-container>
</template>

<script>
import PageContainer from '@/components/PageContainer.vue'
import CommonDataTable from '@/components/CommonDataTable.vue'
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import { db, storage } from '@/firebaseConfig'
import { mapGetters, mapActions } from 'vuex'
import { textFieldRules } from '@/common'
import ImageUpload from '@/components/ImageUpload.vue'
import { v4 as uuidv4 } from 'uuid'
import { objects } from '@/helpers'

export default {
  name: 'ContractorDetails',
  components: {
    PageContainer,
    CommonDataTable,
    CommonEditingPopup,
    ImageUpload,
  },
  props: ['id'],
  data() {
    return {
      title: [
        {
          to: 'contractors',
          text: 'People',
        },
        {
          text: 'View contractor',
        },
      ],
      rules: textFieldRules,
      formValid: false,
      contractorFormValid: false,
      tab: 0,
      saving: false,
      loading: false,
      contractorInfo: {},
      editedContractorInfo: { address: {} },
      unsubscribe: null,
      headers: [
        {
          text: 'Name',
          value: 'name',
          merge: {
            values: ['firstName', 'lastName'],
          },
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Mobile',
          value: 'phoneNumber',
        },
        {
          text: 'Edit',
          value: 'actions',
          sortable: false,
        },
      ],
      addingContact: false,
      imagePreview: {},
      imageToRemoveURL: null,
    }
  },
  created() {
    this.getInfo()
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  computed: {
    ...mapGetters(['companyId']),
    dataBasePath() {
      return `companies/${this.companyId}/contractors/${this.id}`
    },
    contractorInfoChanged() {
      return (
        JSON.stringify(this.contractorInfo) !==
          JSON.stringify(this.editedContractorInfo) || this.imagePreview.file
      )
    },
    headerTitle() {
      return this.title ?? this.contractorInfo.company_name
    },
    docRef() {
      return db
        .collection(`companies/${this.companyId}/contractors`)
        .doc(this.id)
    },
  },
  methods: {
    ...mapActions(['showError', 'showSuccess', 'showDeletionSuccess']),
    async getInfo() {
      this.loading = true
      const ref = this.docRef
      if (this.unsubscribe) {
        this.unsubscribe()
      }
      this.unsubscribe = ref.onSnapshot(
        (doc) => {
          this.contractorInfo = doc.data()
          this.editedContractorInfo = Object.assign(
            { address: {} },
            this.contractorInfo
          )
          this.loading = false
        },
        (error) => {
          console.error(error)
          this.loading = false
        }
      )
    },
    cancelChanges() {
      this.editedContractorInfo = Object.assign(
        { address: {} },
        this.contractorInfo
      )
      this.imageToRemoveURL = null
      this.imagePreview = {}
    },
    imageToRemove(url) {
      this.imageToRemoveURL = url
    },
    getImageRefFromURL(imageURL) {
      const imageURLRef = unescape(/.*\/([^?]+)/.exec(imageURL)[1])
      return imageURLRef
    },
    async deleteRemovedImages() {
      if (this.imageToRemoveURL) {
        const ref = this.getImageRefFromURL(this.imageToRemoveURL)
        const imageRef = storage.ref().child(ref)
        await imageRef.delete()
        delete this.editedContractorInfo.avatarURL
      }
    },
    async uploadNewImages() {
      const file = this.imagePreview?.file
      if (file) {
        const fileName = uuidv4()
        const imageRef = storage
          .ref()
          .child(`companies/${this.companyId}/workforce/${fileName}`)
        const snapshot = await imageRef.put(file)
        const fileURL = await snapshot.ref.getDownloadURL()
        this.editedContractorInfo.logoURL = fileURL
      }
    },
    async saveEditedCompanyInfo() {
      this.saving = true
      try {
        await this.deleteRemovedImages()
        await this.uploadNewImages()

        await this.docRef.update(
          objects.withUpdatedDataInfo(this.editedContractorInfo)
        )
        this.showSuccess('Saved')
      } catch (error) {
        console.error(error)
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async deleteContractor() {
      const result = await this.$confirm(
        `Are you sure you want to delete ${this.contractorInfo.companyName}?`,
        { title: 'Please confirm' }
      )

      if (result === true) {
        // DELETE Contractor
        try {
          await this.docRef.delete()
          this.$router.replace({ name: 'contractors' })
          this.showDeletionSuccess('Contractor info has been deleted.')
        } catch (error) {
          this.showError(
            error.message ?? 'Failed to delete, please try again later.'
          )
        }
      }
    },
    async addContact(newItem) {
      try {
        this.addingContact = true
        const primaryData = await this.docRef
          .collection('contacts')
          .where('primary', '==', true)
          .limit(1)
          .get()

        if (primaryData.docs.length >= 1) {
          const doc = primaryData.docs[0]
          await doc.ref.update(objects.withUpdatedDataInfo({ primary: false }))
        }

        this.$refs.addContactPopup.submitDefault()
        this.showSuccess(
          `Contact <strong>${newItem.firstName} ${
            newItem.lastName
          }</strong> was successfully <strong>${
            newItem.id ? 'updated' : 'added'
          }</strong>`
        )
      } catch (error) {
        console.error(error)
        this.showError(
          error.message ?? 'Failed to add contact, please try again later'
        )
      } finally {
        this.addingContact = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.details-header
  text-align: right
  @include body_dark

.primary-contact
  text-align: right
  @include body_dark

.logo-header
  @include sectionTitle
</style>

